import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import ControlledTextField from "../../../components/ControlledTextField";
import { Col, Form, Row, Button } from "react-bootstrap";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail, 
} from "firebase/auth";
import { CardText, CardTitle } from "reactstrap";
import { toast } from "react-toastify";
import { auth } from "../../../firebase";
import "firebase/auth";
import MailVerifyForm from "../../../components/MailVerifyForm";
import "./index.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import ButtonComponent from "../../../components/ButtonComponent";
import { faGoogle } from "@fortawesome/free-brands-svg-icons";
import SignInImage from "../../../assets/images/login_image.PNG";
import Logo from "../../../assets/images/logo.PNG";
import Swal from "sweetalert2";
import UserForm from "../UserDataForm/userDataForm";
import { useCreateAdminUserMutation } from "../../../redux/services/authService";

// const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL;
type FormData = {
  email: string;
  password: string;
};

const SignIn = () => {
  const navigate = useNavigate();

  //const backgroundImage = `${process.env.REACT_APP_IMAGE_BASE_URL}assets/images/authBackground.PNG`;

  // Update your useForm configuration to include validation
  const { control, handleSubmit, formState: { errors, isValid }, getValues, reset } = useForm<FormData>({
    mode: 'onChange', // This enables real-time validation
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const [showUserForm, setShowUserForm] = useState(false);
  const [formData, setFormData] = useState({
    displayName: "",
    email: "",
    phoneNumber: "",
    photoUrl: "",
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [verificationMessage, setVerificationMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isLogging, setIsLogging] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [tempAccessToken, setTempAccessToken] = useState("");
  const [dots, setDots] = useState("");
  
  // Add forgot password state
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const handleCloseModal = () => setShowModal(false);
  const handleShowModal = () => setShowModal(true);

  const [createAdminUser] = useCreateAdminUserMutation();

  // Toggle to forgot password mode
  const handleForgotPasswordMode = () => {
    setIsForgotPasswordMode(true);
    reset({ email: getValues("email"), password: "" }); // Keep email, clear password
  };

  // Cancel forgot password mode
  const handleCancelForgotPassword = () => {
    setIsForgotPasswordMode(false);
    reset({ email: "", password: "" }); // Clear form
  };

  // Forgot Password Handler
  const handleForgotPassword = async (data: FormData) => {
    setIsResettingPassword(true);
    try {
      await sendPasswordResetEmail(auth, data.email, {
        url: `${window.location.origin}/sign-in`,
        handleCodeInApp: false,
      });
      
      setIsResettingPassword(false);
      
      Swal.fire({
        title: "Password Reset Email Sent!",
        html: `
          <p>We've sent a password reset link to <strong>${data.email}</strong></p>
          <p>Please check your email (including spam/junk folder) and follow the instructions to reset your password.</p>
          <small class="text-muted">If you don't receive the email within a few minutes, please try again.</small>
        `,
        icon: "success",
        confirmButtonColor: "#FF1001",
        confirmButtonText: "OK"
      }).then(() => {
        setIsForgotPasswordMode(false);
        reset({ email: "", password: "" });
      });
      
    } catch (error: any) {
      setIsResettingPassword(false);
      console.error("Password reset error:", error);
      
      let errorMessage = "Failed to send password reset email. Please try again.";
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = "No account found with this email address.";
          break;
        case 'auth/invalid-email':
          errorMessage = "Please enter a valid email address.";
          break;
        case 'auth/too-many-requests':
          errorMessage = "Too many requests. Please try again later.";
          break;
        default:
          errorMessage = error.message || "An error occurred while sending the password reset email.";
      }
      
      toast.error(errorMessage);
    }
  };

  // Handle email and password sign-in
  const handleSignIn = async (data: FormData) => {
    setIsLogging(true);
    try {
      const { email, password } = data;
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Check if email is verified
      if (!userCredential.user.emailVerified) {
        handleShowModal();
        setIsLogging(false);
        return;
      }

      // Get user roles
      const idTokenResult = await userCredential.user.getIdTokenResult();
      const roles: string[] = (idTokenResult.claims.roles as string[]) || [];

      const lowercaseRoles = roles.map(role => role.toLowerCase());


      // Check if user has admin or super_admin role
      if (!lowercaseRoles.includes('admin') && !lowercaseRoles.includes('super_admin') && !lowercaseRoles.includes('planner') && !lowercaseRoles.includes('call_center_agent')) {
        setIsLogging(false);

        Swal.fire({
          title: "You don't have permission to access this portal. Do you want to setup an admin account?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes",
          cancelButtonText: "No",
        }).then(async (result: any) => {
          if (result.isConfirmed) {
            setFormData({
              displayName: userCredential.user.displayName || "",
              email: userCredential.user.email || "",
              phoneNumber: userCredential.user.phoneNumber || "",
              photoUrl: userCredential.user.photoURL || "",
            });
            setShowUserForm(true);
          } else {
            // Only sign out if they choose not to proceed
            await auth.signOut();
            localStorage.clear();
          }
        });
        return;
      }

      // Store tokens and user info
      const accessToken = await userCredential.user.getIdToken();
      localStorage.setItem("accessToken", accessToken);

      if (userCredential.user.email) {
        localStorage.setItem("email", userCredential.user.email);
      }
      if (userCredential.user.photoURL) {
        localStorage.setItem("photoURL", userCredential.user.photoURL);
      }
      if (userCredential.user.uid) {
        localStorage.setItem("ownerID", userCredential.user.uid);
      }

      // Dispatch custom event to notify navigation of auth state change
      window.dispatchEvent(new CustomEvent('authStateChanged'));

      // Redirect based on role
      if (roles.includes('super_admin')) {
        navigate('/home');
        toast.success("Welcome Admin!");
      } else {
        navigate('/home');
        toast.success("Welcome Admin!");
      }

      setIsLogging(false);
    } catch (error: any) {
      setIsLogging(false);
      if (
        error.code === "auth/wrong-password" ||
        error.code === "auth/user-not-found" || error.code === "auth/invalid-credential"
      ) {
        toast.error("Please check your email and password.");
      } else if (error.code === "auth/too-many-requests") {
        toast.error("Too many attempts. Please try again later.");
      } else {
        toast.error("Something went wrong, please try again.");
      }
    }
  };

  // Handle Google sign-in
  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: "select_account",
    });
    try {
      const userCredential = await signInWithPopup(auth, provider);

      // Check if email is verified
      if (!userCredential.user.emailVerified) {
        handleShowModal();
        return;
      }

      // Get user roles
      const idTokenResult = await userCredential.user.getIdTokenResult();
      const roles: string[] = (idTokenResult.claims.roles as string[]) || [];

      // Check if user has admin or super_admin role
      if (!roles.includes('admin') && !roles.includes('super_admin') && !roles.includes('planner') && !roles.includes('call_center_agent')) {
        await auth.signOut();
        localStorage.clear();
        toast.error("You don't have permission to access this portal");
        return;
      }

      // Store tokens and user info
      const accessToken = await userCredential.user.getIdToken();
      setTempAccessToken(accessToken);
      localStorage.setItem("accessToken", accessToken);

      if (userCredential.user.uid) {
        localStorage.setItem("ownerID", userCredential.user.uid);
      }
      if (userCredential.user.email) {
        localStorage.setItem("email", userCredential.user.email);
      }
      if (userCredential.user.photoURL) {
        localStorage.setItem("photoURL", userCredential.user.photoURL);
      }

      // Dispatch custom event to notify navigation of auth state change
      window.dispatchEvent(new CustomEvent('authStateChanged'));

      // Redirect based on role
      if (roles.includes('super_admin')) {
        navigate('/home');
        toast.success("Welcome Admin!");
      } else {
        navigate('/home');
        toast.success("Welcome Admin!");
      }
    } catch (error: any) {
      console.error("Google sign-in error:", error);
      toast.error(error.message || "Failed to sign in with Google.");
    }
  };

  // Form submit handler - handles both sign in and forgot password
  const onSubmit = (data: FormData) => {
    if (isForgotPasswordMode) {
      handleForgotPassword(data);
    } else {
      handleSignIn(data);
    }
  };

  const handleUserFormSubmit = async (data: {
    displayName?: string;
    email?: string;
    phoneNumber?: string;
    photoUrl?: string;
  }) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("User not authenticated");
      }

      const accessToken = await currentUser.getIdToken();
      localStorage.setItem("accessToken", accessToken);
      const adminUserData = {
        userId: auth.currentUser?.uid || "",
        displayName: data.displayName || formData.displayName,
        email: data.email || formData.email,
        phoneNumber: data.phoneNumber || formData.phoneNumber,
        photoUrl: data.photoUrl || formData.photoUrl,
      };

      // Call your API to create admin user
      await createAdminUser(adminUserData).unwrap();
      await auth.signOut();
      localStorage.clear();

      toast.success("Admin account request submitted successfully!");
      setShowUserForm(false);
      navigate("/sign-in");
    } catch (error) {
      console.error("Error creating admin account:", error);
      toast.error("Failed to setup admin account");
    }
  };

  useEffect(() => {
    if (!isLogging && !isResettingPassword) return;

    const interval = setInterval(() => {
      setDots((prev) => (prev.length < 3 ? prev + "." : ""));
    }, 500);
    return () => clearInterval(interval);
  }, [isLogging, isResettingPassword]);

  return (
    <div className="signUpBackground">
      <div className="auth-wrapper auth-cover">
        <Row className="auth-inner m-0">
          <Col className="d-none d-lg-flex align-items-center" lg="7" sm="12">
            <div className="w-100 d-lg-flex justify-content-center">
              <img
                className=""
                // src={`${IMAGE_BASE_URL}/assets/images/login_image.PNG`}
                src={SignInImage}
                alt="Login Cover"
                style={{ height: "100vh" }}
              />
            </div>
          </Col>
          <Col
            className="d-flex align-items-center auth-bg px-2 p-lg-5"
            lg="5"
            sm="12"
          >
            <Col className="px-xl-2 mx-auto" sm="8" md="6" lg="12">
              <div className="imageAreaBackgroundColor">
                <img
                  className="mb-2"
                  // src={`${IMAGE_BASE_URL}/assets/images/logo.PNG`}
                  src={Logo}
                  alt="logo"
                  style={{ width: "100%" }}
                />
              </div>
              
              {/* Dynamic Title and Description */}
              <CardTitle tag="h2" className="fw-bold mb-1">
                {isForgotPasswordMode ? "Reset Your Password 🔐" : "Welcome to Curry King Admin ! 👋"}
              </CardTitle>
              <CardText className="mb-2">
                {isForgotPasswordMode 
                  ? "Enter your email address and we'll send you a link to reset your password."
                  : "Please sign-in to your account and start the adventure"
                }
              </CardText>
              
              <Form
                className="auth-login-form mt-2"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="mb-3">
                  <ControlledTextField
                    label="Email"
                    inputName="email"
                    inputType="email"
                    placeholder="Enter Your Email"
                    isRequired={true}
                    control={control}
                    rules={{
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address"
                      }
                    }}
                  />
                  {errors.email && (
                    <div className="text-danger mt-1 small">
                      {errors.email.message}
                    </div>
                  )}
                </div>
                
                {/* Password field - only show in sign-in mode */}
                {!isForgotPasswordMode && (
                  <div className="mb-4">
                    <ControlledTextField
                      label="Password"
                      inputName="password"
                      inputType="password"
                      placeholder="Enter Password"
                      isRequired={true}
                      control={control}
                      rules={{
                        required: "Password is required",
                        minLength: {
                          value: 6,
                          message: "Password must be at least 6 characters"
                        }
                      }}
                    />
                    {errors.password && (
                      <div className="text-danger mt-1 small">
                        {errors.password.message}
                      </div>
                    )}
                  </div>
                )}

                {/* Forgot Password Mode Buttons */}
                {isForgotPasswordMode ? (
                  <Row className="mb-3">
                    <Col>
                      <Button
                        variant="outline-secondary"
                        onClick={handleCancelForgotPassword}
                        disabled={isResettingPassword}
                        className="me-2"
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={!isValid || isResettingPassword}
                        className="btn my-2 py-2 btn-signin-color btn-block"
                      >
                        {isResettingPassword ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                            Sending{dots}
                          </>
                        ) : (
                          "Send Reset Link"
                        )}
                      </Button>
                    </Col>
                  </Row>
                ) : (
                  <>
                    {/* Sign In Button */}
                    <div className="d-grid">
                      {isLogging ? (
                        <button
                          type="submit"
                          className="btn my-2 py-2 btn-signin-color btn-block"
                          disabled={true}
                        >
                          <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                          Logging{dots}
                        </button>
                      ) : (
                        <button
                          type="submit"
                          className="btn my-2 py-2 btn-signin-color btn-block"
                          disabled={!isValid}
                        >
                          Sign In
                        </button>
                      )}
                    </div>

                    {/* Forgot Password Link */}
                    <div className="d-flex justify-content-center mb-3">
                      <button
                        type="button"
                        className="btn btn-link text-decoration-none p-0"
                        style={{ color: "#FF1001", fontSize: "14px" }}
                        onClick={handleForgotPasswordMode}
                      >
                        Forgot Password?
                      </button>
                    </div>

                    {/* Sign In with Google */}
                    <div className="text-center my-3">
                      <hr className="my-2" />
                      <span>or continue</span>
                    </div>
                    <div className="d-flex justify-content-center mt-3 w-100">
                      <ButtonComponent
                        titel="Sign In with Google"
                        type="button"
                        onClick={handleGoogleSignIn}
                        icon={<FontAwesomeIcon icon={faGoogle} />}
                        fullWidth={true}
                      />
                    </div>
                  </>
                )}
              </Form>
            </Col>
          </Col>
        </Row>
      </div>

      {/* Existing Modals */}
      <MailVerifyForm show={showModal} handleClose={handleCloseModal} />
      {showUserForm && (
        <UserForm
          showModal={showUserForm}
          handleModalClose={() => setShowUserForm(false)}
          handleSubmit={handleUserFormSubmit}
          displayName={formData.displayName}
          email={formData.email}
          phoneNumber={formData.phoneNumber}
          photoUrl={formData.photoUrl}
          verificationMessage={verificationMessage}
        />
      )}
    </div>
  );
};

export default SignIn;